/* eslint-disable complexity */
/**
 * 文件消息组件
 * 使用方：用户对话，群组对话
 * @author sunzhiguang
 * @date 2020/6/10
 */

import React, { useContext, useEffect, useMemo, useState } from "react";
import "./index.less";
import IconFont from "@/components/icon";
import { Dropdown, Menu, message as toast, Popover, Progress, Tooltip } from "antd";
import { useTranslation, WithTranslation } from "react-i18next";
import {
  ChatMessage,
  ChatMessageType,
  Employee,
  FileChatMessage,
  FileStatus,
  MessageStatus
} from "@/types/chat";
import {
  downLoadFile,
  formatFileName,
  getFileIconByMime,
  getFileName,
  getMessageFileName,
  checkDownloadFilePathValid,
  getFileFilter,
  getFileUrl,
  sizeToStr,
  downloadMessageFile,
  getPreviewCanEdit
} from "@/utils/chat/index";
import { MessageDateFormType, MessageSendType } from "@/types/chat/enum";
import { connect } from "dva";
import ChatState from "@/types/chat/State";
import { lookup } from "mime-types";
import { DownloadItem, FocusSDK, isFocusEnv } from "@/utils";
import ImService from "@/server/ImService";
import { getMessageDateFormat } from "@/utils/date";
import { formatMatchContext } from "@/components/chat/MessageHistorySearch/utils";
import GlobalContext, { GlobalContextConfig } from "@/context/GlobalContext";
import {
  isEqualEmployee,
  isCrossPlatformGroupSession,
  isOuterContacts,
  parseMessageToUserBySingle,
  getFileDownloadBzOptions
} from "@/utils/chat/message";
import {
  handleAddFileDownloadRecord,
  checkFileIsExpired
} from "@/components/chat/utils/commonMessageModal";
import { SaveOption } from "@/utils/DownloadFileQueue";
import { addXclient } from "@/utils/addToken";
import egovFileIcon from "@/assets/images/im/egov-file-icon.png";
import { tagsNode } from "@/components/AddressBook/common";
import {
  changeEgovUrl,
  crossAppSwitch,
  getFileOperate,
  getFileOperateAuthority,
  getFileTagName
} from "@/utils/tools";
import { canJoyspacePreview, openJoyspacePreview } from "@/components/Joyspace/util";
import { getWpsPreviewCode } from "@/api/chat";
import { debounce } from "lodash";
import ChatContext from "@/components/chat/context";
import { FileTipTag } from "@/components/Tags";
import { FILE_DOWNLOAD_BIZTYPE } from "@/utils/chat/message";

interface DownLoadFileProps {
  message: FileChatMessage;
  sendType: MessageSendType;
  searchKey: string;
  currentEmployee: Employee;
  jumpTo?: () => void;
  updateSessionMessageMid: Function;
  isHistoryAllTab?: boolean;
  handleExpiredFileClick: (mid: number) => void;
  expiredMids?: number[];
}

type IDownLoadFileProps = Readonly<WithTranslation & DownLoadFileProps & ChatState>;

let dXhr: any = {};
function DownLoadFile(props: IDownLoadFileProps) {
  const {
    message,
    searchKey,
    sendType,
    currentEmployee,
    selectedSession,
    updateSessionMessageMid,
    isHistoryAllTab,
    handleExpiredFileClick,
    expiredMids = [],
    jumpTo
  } = props;
  const { currentFileControl, fileTagList } = React.useContext(ChatContext);
  const { t } = useTranslation("chat");
  const [status, setStatus] = useState(FileStatus.INIT);
  const [path, setPath] = useState("");
  const [progress, setProgress] = useState(0);
  const [downloadItem, setDownloadItem]: any = useState({});
  const [isWpsFile, setIsWpsFile] = useState(false);
  const [isExpired, setIsExpired] = useState(false);
  const { appRuntimeEnv, localConfigData } = useContext<GlobalContextConfig>(GlobalContext);
  const {
    joyspaceHost,
    im,
    fileService: { imAppKey },
    joyspaceWpsTarget
  } = appRuntimeEnv;
  const defaultDownloadPath =
    appRuntimeEnv.registryConfig.defaultDownloadPath || FocusSDK.getDownloadPath();
  const imDownloadSaveAs = localConfigData?.settings?.imDownloadSaveAs;
  const isOutApp = selectedSession.isGroup
    ? isCrossPlatformGroupSession(selectedSession)
    : isOuterContacts(selectedSession, currentEmployee);
  const isDisplay = !isOutApp && crossAppSwitch("joyspace");
  useEffect(() => {
    const downLocalPath = message.downLocalPath || message.upLocalPath;
    if (isFocusEnv()) {
      if (downLocalPath) {
        FocusSDK.checkLocalPathExist(downLocalPath, (exists: boolean) => {
          if (exists) {
            setStatus(FileStatus.COMPLETED);
            setPath(downLocalPath || "");
          } else {
            setStatus(FileStatus.INIT);
            setPath("");
          }
        });
      } else {
        setStatus(FileStatus.INIT);
        setPath("");
      }
    }
  }, [message.downLocalPath, message.upLocalPath]);

  useEffect(() => {
    if (message.name) {
      const res = canJoyspacePreview(message.name);
      setIsWpsFile(res);
    }
  }, [message]);

  useEffect(() => {
    setIsExpired(message.statusType === MessageStatus.EXPIRED || expiredMids.includes(message.mid));
  }, [message.statusType, expiredMids, message]);

  const getDownloadOptions = ({ viewFile, saveAs, message, auto }: SaveOption) => {
    return {
      onStarted: (downloadItem: DownloadItem) => {
        setDownloadItem(downloadItem);
      },
      onProgress: ({ percent = 0 }) => {
        setStatus(FileStatus.DOWNLOADING);
        setProgress(percent);
      },
      onCancel: () => {},
      onPause: () => {
        setStatus(FileStatus.CANCELED);
      },
      onResume: () => {
        setStatus(FileStatus.DOWNLOADING);
      },
      onFinish: async (file: { path: string; filename: string }, data: any) => {
        const saveName = getMessageFileName(message);
        if (!checkDownloadFilePathValid(saveName, file)) {
          setStatus(FileStatus.INIT);
          toast.error(t("failed"));
          return;
        }
        const imService = ImService.getInstance();
        const result = await imService.updateLocalPath(message.sessionId, message.mid, {
          downLocalPath: file.path
        });

        if (result) {
          setStatus(FileStatus.COMPLETED);
          setProgress(100);
          setPath(file.path || "");
          updateSessionMessageMid({
            sessionId: message.sessionId,
            sendResult: { body: { ...message, downLocalPath: file.path } }
          });
          if (!isEqualEmployee(message.sender, currentEmployee)) {
            handleAddFileDownloadRecord(message, currentEmployee, 0, selectedSession);
          }
        } else {
          toast.error(t("failed"));
        }
      },
      onError: (error: any) => {
        if (error.errorCode === 1017005) {
          const imService = ImService.getInstance();
          imService.updateMessage(message.sessionId, message.mid, {
            statusType: MessageStatus.EXPIRED
          });
          updateSessionMessageMid({
            sessionId: message.sessionId,
            sendResult: { body: { ...message, statusType: MessageStatus.EXPIRED } }
          });
          setIsExpired(true);
        }
      }
    };
  };

  const doDownloadMessageFile = ({
    viewFile,
    saveAs,
    message,
    auto = false,
    currentEmployee = undefined
  }: SaveOption): Promise<void> => {
    const options = getDownloadOptions({ viewFile, saveAs, message, auto });
    return downloadMessageFile({ viewFile, saveAs, message, auto, currentEmployee }, options);
  };

  const hasAuthPreview = useMemo(() => {
    const auth = getFileOperateAuthority(
      "onlinePreview",
      currentFileControl || [],
      message?.fileTag || 0
    );
    return auth;
  }, [currentFileControl, message.fileTag]);

  const canWpsPreview = useMemo(() => {
    return hasAuthPreview && isDisplay && !selectedSession.isSecret && joyspaceWpsTarget === 'private';
  }, [hasAuthPreview, isDisplay, selectedSession.isSecret]);

  const getFileConfig = (status: FileStatus): string[] => {
    if (canWpsPreview && isWpsFile && message.type !== ChatMessageType.EGOV_FILE) {
      return ["preview", ""];
    }
    const data: any = {
      [FileStatus.INIT]: getFileOperate(
        [
          "click-to-save", // 下载
          "" //
        ],
        currentFileControl || [],
        message.fileTag || 0
      ),
      [FileStatus.DOWNLOADING]: ["cancel-download", ""],
      [FileStatus.CANCELED]: ["download-resume", ""],
      [FileStatus.FAILED]: ["re-download", ""],
      [FileStatus.NOTHAVE]: ["re-download", ""],
      [FileStatus.COMPLETED]: isFocusEnv()
        ? ["click-to-view", "open-folder"]
        : getFileOperate(["click-to-save"], currentFileControl || [], message.fileTag || 0)
    };
    return data[status];
  };

  const canJoyspaceEdit = useMemo(() => {
    const auth = getFileOperateAuthority(
      "tranferTo",
      currentFileControl || [],
      message?.fileTag || 0
    );
    return auth ? 1 : 0;
  }, [currentFileControl, message.fileTag]);

  // eslint-disable-next-line complexity
  const fileOperate = async (operateKey: string) => {
    let isExpired;
    switch (operateKey) {
      case "preview":
        if (canWpsPreview) {
          isExpired = checkFileIsExpired({
            expiredAppKey: imAppKey,
            message,
            updateSessionMessageMid,
            expiredTime: im.fileExpiredTime
          });
          if (isExpired) {
            setIsExpired(true);
            handleExpiredFileClick(message.mid);
            return;
          }
          if (isWpsFile) {
            let previewParams: any = {
              url: message.url,
              mid: message.mid,
              groupId: selectedSession.sessionId
            };
            previewParams = {
              ...previewParams,
              id: message.id,
              fileName: message.name,
              from: {
                pin: message.from?.pin,
                app: message.from?.app,
                teamId: message.from?.teamId
              }
            };
            if (selectedSession.isSingle) {
              const toUser = parseMessageToUserBySingle(selectedSession.sessionId, {
                ...message.from,
                userId: message.from?.pin
              } as any);
              console.log(toUser);
              previewParams.to = toUser;
            }
            getWpsPreviewCode(previewParams).then((res) => {
              if (res[0]) {
                handleAddFileDownloadRecord(message, currentEmployee, 1, selectedSession);
                openJoyspacePreview({
                  host: joyspaceHost,
                  code: res[0].code,
                  isShowHeader: 1,
                  canSaveToJoyspace: canJoyspaceEdit,
                  gid: selectedSession.sessionId,
                  gname: selectedSession.sessionId ? selectedSession.name : "",
                  canEdit: canJoyspaceEdit && getPreviewCanEdit(previewParams.fileName),
                  canForward: 1
                });
              } else {
                toast.error(res[1] || "网络异常");
              }
            });
          } else {
            toast.info("暂不支持预览此类型文件");
          }
        } else if (!hasAuthPreview) {
          toast.info(t("file-secret-tip"));
          return;
        }
        break;
      case "click-to-save":
        isExpired = checkFileIsExpired({
          expiredAppKey: imAppKey,
          message,
          updateSessionMessageMid,
          expiredTime: im.fileExpiredTime
        });
        if (isExpired) {
          handleExpiredFileClick(message.mid);
          setIsExpired(true);
          return;
        }
        if (!FocusSDK.isPrivateNet() && message.type === ChatMessageType.EGOV_FILE) {
          toast.error(t("egov-file-notice"));
          return;
        }
        if (isFocusEnv()) {
          doDownloadMessageFile({
            viewFile: true,
            saveAs: imDownloadSaveAs,
            message,
            currentEmployee
          })
            .then()
            .catch((e) => console.log(e));
        } else {
          // web 端下载文件
          const name = getFileName(message);
          const url = await addXclient(
            getFileUrl(message.url, getFileDownloadBzOptions(selectedSession, currentEmployee))
          );
          dXhr = downLoadFile(name, url, "", {
            onProgress: (currPercent: any) => {
              setStatus(FileStatus.DOWNLOADING);
              setProgress(currPercent);
            },
            onFinish: () => {
              setStatus(FileStatus.COMPLETED);
              setProgress(100);
              dXhr = {};
              toast.success("下载成功");
              handleAddFileDownloadRecord(message, currentEmployee, 0, selectedSession);
            }
          });
        }
        break;
      case "download-resume":
        if (!FocusSDK.isPrivateNet() && message.type === ChatMessageType.EGOV_FILE) {
          toast.error(t("egov-file-notice"));
          return;
        }
        if (isFocusEnv()) {
          downloadItem?.resume();
        } else {
          // web 端下载文件
          const name = getFileName(message);
          const url = await addXclient(
            getFileUrl(message.url, getFileDownloadBzOptions(selectedSession, currentEmployee))
          );
          dXhr = downLoadFile(name, url, "", {
            onProgress: (currPercent: any) => {
              setStatus(FileStatus.DOWNLOADING);
              setProgress(currPercent);
            },
            onFinish: () => {
              setStatus(FileStatus.COMPLETED);
              setProgress(100);
              dXhr = {};
              toast.success("下载成功");
              handleAddFileDownloadRecord(message, currentEmployee, 0, selectedSession);
            }
          });
        }
        break;
      case "click-to-view":
        if (isFocusEnv() && path) {
          FocusSDK.checkLocalPathExist(path, async (exists: boolean) => {
            if (exists) {
              FocusSDK.openItem(path);
            } else {
              isExpired = checkFileIsExpired({
                expiredAppKey: imAppKey,
                message,
                updateSessionMessageMid,
                expiredTime: im.fileExpiredTime
              });
              if (isExpired) {
                handleExpiredFileClick(message.mid);
                setIsExpired(true);
                return;
              }
              if (!FocusSDK.isPrivateNet() && message.type === ChatMessageType.EGOV_FILE) {
                toast.error(t("egov-file-notice"));
                return;
              }
              setStatus(FileStatus.DOWNLOADING);
              setProgress(0);
              setTimeout(() => {
                doDownloadMessageFile({ viewFile: true, saveAs: false, message, currentEmployee })
                  .then()
                  .catch((e) => console.log(e));
              }, 50);
            }
          });
        }
        break;
      case "open-folder":
        if (isFocusEnv() && path) {
          FocusSDK.checkLocalPathExist(path, async (exists: boolean) => {
            if (exists) {
              FocusSDK.showItemInFolder(path);
            } else {
              isExpired = checkFileIsExpired({
                expiredAppKey: imAppKey,
                message,
                updateSessionMessageMid,
                expiredTime: im.fileExpiredTime
              });
              if (isExpired) {
                handleExpiredFileClick(message.mid);
                setIsExpired(true);
                return;
              }
              if (!FocusSDK.isPrivateNet() && message.type === ChatMessageType.EGOV_FILE) {
                toast.error(t("egov-file-notice"));
                return;
              }
              // setStatus(FileStatus.DOWNLOADING);
              // setProgress(0);
              setTimeout(() => {
                doDownloadMessageFile({ viewFile: false, saveAs: true, message, currentEmployee })
                  .then()
                  .catch((e) => console.log(e));
              }, 50);
            }
          });
        }
        break;
      case "cancel-download":
        if (isFocusEnv()) {
          downloadItem?.pause();
        } else {
          dXhr.abort?.();
          setStatus(FileStatus.CANCELED);
          setProgress(0);
        }
        break;
      default:
        if (!hasAuthPreview) {
          toast.info(t("file-secret-tip"));
          return;
        }
        break;
    }
  };

  const [operateKey1, operateKey2] = getFileConfig(status);
  const [fileIcon] = useMemo(
    () => getFileIconByMime(message.fileType || lookup(message.name) || ""),
    [message]
  );

  const isEgovFile = useMemo(() => {
    return message.type === ChatMessageType.EGOV_FILE || message.kind === ChatMessageType.EGOV_FILE;
  }, [message]);

  const showFileTag = useMemo(() => {
    return isEgovFile || (message?.fileTag || 0) !== 0;
  }, [isEgovFile, message.fileTag]);

  const fileTagNameComp = useMemo(() => {
    if (!showFileTag) {
      return null;
    }
    const tagName = getFileTagName({
      allName: true,
      fileTag: message?.fileTag,
      fileTagList,
      isEgovFile
    });
    return FileTipTag(tagName, message?.fileTag || 0, true);
  }, [showFileTag, message.fileTag, fileTagList, isEgovFile]);

  return (
    <div className="history-file">
      <div className="file-icon-container">
        <img src={changeEgovUrl(fileIcon)} alt="" className="file-icon" draggable="false" />
        {/* {message.type === ChatMessageType.EGOV_FILE && (
          <img src={changeEgovUrl(egovFileIcon)} className="egov-file-icon" alt="" />
        )} */}
        {status === FileStatus.DOWNLOADING && (
          <div
            className="file-img-mask"
            style={{
              backgroundImage: `conic-gradient(transparent ${
                progress * 3.6
              }deg,  rgba(0, 0, 0, 0.3) ${progress * 3.6}deg)`
            }}
          >
            {/* <IconFont
                        type="iconlog_del"
                        className="file-content-download-cancel"
                        onClick={() => {
                            fileOperate(operateKey);
                        }}
                    /> */}
          </div>
        )}
      </div>
      <div
        className="file-content"
        onClick={debounce(() => {
          if (!isHistoryAllTab) {
            fileOperate(operateKey1);
          }
        }, 500)}
      >
        <div className="file-content-name">{formatMatchContext(message.name, searchKey)}</div>
        <div className="file-content-status">
          <div
            className={`${
              isHistoryAllTab ? "file-info-status justify-between" : "file-info-status"
            }`}
          >
            <div className="file-info-container">
              <span className="file-info-size">{sizeToStr(message.size)}</span>
              {!isHistoryAllTab && (
                <>
                  <span className="file-info-uploader">
                    {formatMatchContext(message.sender.name, searchKey)}
                    {tagsNode(message.sender.labelInfoList)}
                  </span>
                  <span className="file-info-time">
                    {getMessageDateFormat(
                      message.timestamp,
                      MessageDateFormType.DATE_AGGREGATION,
                      ""
                    )}
                  </span>
                </>
              )}
            </div>
            {isExpired && <span className="file-info-expired">{t("file-expired")}</span>}
            {fileTagNameComp}
          </div>
        </div>
      </div>

      {!isHistoryAllTab && (
        <Dropdown
          overlay={
            <Menu>
              <Menu.Item
                className="history-menu-item"
                // key="download"
                onClick={(e) => {
                  e.domEvent.stopPropagation();
                  setTimeout(() => {
                    jumpTo?.();
                  }, 0);
                }}
              >
                定位到聊天位置
              </Menu.Item>
              {!!operateKey1 && (
                <Menu.Item
                  className="history-menu-item"
                  // key="download"
                  onClick={(e) => {
                    e.domEvent.stopPropagation();
                    fileOperate(operateKey1);
                  }}
                >
                  {t(`history-${operateKey1}`)}
                </Menu.Item>
              )}
              {!!operateKey2 && (
                <Menu.Item
                  className="history-menu-item"
                  // key="download"
                  onClick={(e) => {
                    e.domEvent.stopPropagation();
                    fileOperate(operateKey2);
                  }}
                >
                  {t(`history-${operateKey2}`)}
                </Menu.Item>
              )}
            </Menu>
          }
          placement="bottomRight"
          trigger={["hover"]}
        >
          <IconFont type="iconlog_more" className="file-actions" />
        </Dropdown>
      )}
    </div>
  );
}

function mapStateToProps({ chat }: any) {
  const { selectedSession, currentEmployee } = chat as ChatState;
  return { selectedSession, currentEmployee };
}

function mapDispatchToProps(dispatch: any) {
  return {
    updateSessionMessageMid(data: { message: ChatMessage; sessionId: string }) {
      dispatch({ type: "chat/updateSessionMessageMid", payload: data });
    }
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(DownLoadFile);
